import React, { Component, Suspense } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ReactTable from "react-table";
import { Button, ButtonGroup, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popconfirm } from "antd";

import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import { closeCustomModal, openCustomModal } from "components/custom/modals";
import {
	fetchInterviewQuestions,
	clearInterviewQuestions,
	deleteInterviewQuestion
} from "api/menu/hr/interview-questions/actions";
import { fetchInterviewCategories } from "api/menu/hr/interview-categories/actions";

const QuestionModal = React.lazy(() => import("./question-modal"));

class InterviewQuestionsList extends Component {
	constructor(props) {
		super(props);
		this.state = {
			categoryFilter: "",
			includeInactive: false
		};
	}

	componentDidMount() {
		this.props.fetchInterviewCategories(false);
		this.loadQuestions();
	}

	componentWillUnmount() {
		this.props.clearInterviewQuestions();
	}

	loadQuestions = () => {
		const categoryId = this.state.categoryFilter ? parseInt(this.state.categoryFilter, 10) : null;
		this.props.fetchInterviewQuestions(categoryId, this.state.includeInactive);
	};

	openModal = (id = false) => {
		const modalTitle = id !== false ? "Update" : "Create";
		const modalObj = {
			id: `interview-question-${id}`,
			header: `${modalTitle} Interview Question`,
			modalSettings: { size: "lg" },
			children: (
				<Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
					<QuestionModal
						questionId={id}
						onCancel={() => this.props.closeCustomModal(modalObj)}
						onSaved={() => {
							this.loadQuestions();
							this.props.closeCustomModal(modalObj);
						}}
					/>
				</Suspense>
			),
			handleClose: () => this.props.closeCustomModal(modalObj)
		};
		this.props.openCustomModal(modalObj);
	};

	delete = (id) => {
		this.props.deleteInterviewQuestion(id, () => this.loadQuestions());
	};

	render() {
		const { questions, loading, categories } = this.props;
		const columns = [
			{ Header: "Category", accessor: "categoryName", id: "categoryName" },
			{
				Header: "Question",
				accessor: "text",
				id: "text",
				minWidth: 300
			},
			{
				Header: "Required",
				id: "isRequired",
				accessor: original => (original.isRequired ? "Yes" : "No"),
				Cell: ({ original }) => (original.isRequired ? "Yes" : "No"),
				className: "d-flex justify-content-center align-items-center",
				width: 110,
				Filter: ({ filter, onChange }) => (
					<ReactTableFilterSelect
						currentValue={filter ? filter.value : ""}
						onChange={value => onChange(value)}
						style={{ width: "100%", height: "100%" }}
						optionsObject={{ "": "Show All", YES: "Yes", NO: "No" }}
					/>
				)
			},
			{
				Header: "Order",
				id: "requiredOrder",
				accessor: original => (original.isRequired ? original.requiredOrder : null),
				Cell: ({ original }) => (
					original.isRequired && original.requiredOrder != null ? original.requiredOrder : "—"
				),
				className: "d-flex justify-content-center align-items-center",
				width: 90,
				filterable: false
			},
			{
				Header: "Active",
				id: "isActive",
				accessor: original => (original.isActive ? "Yes" : "No"),
				Cell: ({ original }) => (original.isActive ? "Yes" : "No"),
				className: "d-flex justify-content-center align-items-center",
				Filter: ({ filter, onChange }) => (
					<ReactTableFilterSelect
						currentValue={filter ? filter.value : ""}
						onChange={value => onChange(value)}
						style={{ width: "100%", height: "100%" }}
						optionsObject={{ "": "Show All", YES: "Yes", NO: "No" }}
					/>
				)
			},
			{
				Header: "Actions",
				id: "actions",
				className: "d-flex justify-content-center align-items-center",
				Cell: ({ original }) => (
					<ButtonGroup>
						<Button size="sm" onClick={() => this.openModal(original.id)}>
							<FontAwesomeIcon icon="pen" title="Update" />
						</Button>
						<Popconfirm
							title="Delete this question?"
							onConfirm={() => this.delete(original.id)}
							placement="bottomRight"
							okText="Yes"
							cancelText="Cancel"
						>
							<Button variant="danger" size="sm">
								<FontAwesomeIcon icon="times" />
							</Button>
						</Popconfirm>
					</ButtonGroup>
				),
				filterable: false,
				width: 100
			}
		];

		return (
			<div className="dashboard">
				<h3 className="mb-4 d-flex align-items-center">
					Interview Questions
					<Button className="ml-auto" variant="primary" onClick={() => this.openModal()}>
						Add Question
					</Button>
				</h3>
				<div className="d-flex mb-3 align-items-center">
					<Form.Control
						as="select"
						className="mr-2"
						style={{ maxWidth: 260 }}
						value={this.state.categoryFilter}
						onChange={(e) => this.setState({ categoryFilter: e.target.value }, this.loadQuestions)}
					>
						<option value="">All categories</option>
						{(categories || []).map(c => (
							<option key={c.id} value={c.id}>{c.name}</option>
						))}
					</Form.Control>
					<Form.Check
						type="checkbox"
						label="Include inactive"
						checked={this.state.includeInactive}
						onChange={(e) => this.setState({ includeInactive: e.target.checked }, this.loadQuestions)}
					/>
				</div>
				<ReactTable
					data={questions}
					headerClassName="table-header"
					columns={columns}
					defaultPageSize={20}
					pageSizeOptions={[10, 20, 50]}
					resizable
					className="-striped -highlight"
					loading={loading}
					filterable
					defaultFilterMethod={filterCaseInsensitive}
					defaultSorted={[
						{ id: "isRequired", desc: true },
						{ id: "requiredOrder", desc: false },
						{ id: "categoryName", desc: false }
					]}
					getNoDataProps={() => ({ style: { zIndex: 0 } })}
					style={{ height: "calc(100vh - 200px)" }}
				/>
			</div>
		);
	}
}

InterviewQuestionsList.propTypes = {
	questions: PropTypes.array.isRequired,
	categories: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired,
	fetchInterviewQuestions: PropTypes.func.isRequired,
	fetchInterviewCategories: PropTypes.func.isRequired,
	clearInterviewQuestions: PropTypes.func.isRequired,
	deleteInterviewQuestion: PropTypes.func.isRequired,
	openCustomModal: PropTypes.func.isRequired,
	closeCustomModal: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
	questions: state.interviewQuestions.questions,
	categories: state.interviewCategories.categories,
	loading: state.interviewQuestions.loading
});

const mapDispatchToProps = dispatch => ({
	fetchInterviewQuestions: bindActionCreators(fetchInterviewQuestions, dispatch),
	fetchInterviewCategories: bindActionCreators(fetchInterviewCategories, dispatch),
	clearInterviewQuestions: bindActionCreators(clearInterviewQuestions, dispatch),
	deleteInterviewQuestion: bindActionCreators(deleteInterviewQuestion, dispatch),
	openCustomModal: bindActionCreators(openCustomModal, dispatch),
	closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(InterviewQuestionsList);
